import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  Router,
  TitleStrategy,
  withComponentInputBinding,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Location } from '@angular/common';
import {
  CountriesEffects,
  CountryOptionsEffects,
  CurrentUserEffects,
  DamageEntryEffects,
  LocationsEffects,
  PartsManagerEffects,
  ProcessPartnersEffects,
  RecentSurveysEffects,
  RoleOptionsEffects,
  TranslationsEffects,
  UploadFilesEffects,
  UsersEffects,
  VehicleMovementsEffects,
} from './state/effects.index';
import {
  authInitializerFactory,
  FEDEV_AUTH_INIT_OPTIONS,
  FedevAuthService,
  provideFedevAuth,
} from '@bmw-fedev/auth';
import { environment } from 'src/environments/environment';
import { DsFormFieldModule, DsImprintModule } from '@bmw-ds/components';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { CustomTranslateLoaderService } from './common/custom-translate-loader.service';
import { provideStore, RuntimeChecks, Store } from '@ngrx/store';
import { AppState } from './state/app.state';
import { initTranslations } from './common/init-translations';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { currentUserReducer } from './state/current-user/current-user.reducer';
import { translationsReducer } from './admin/translations/state/translations.reducer';
import { usersReducer } from './admin/users/state/users.reducer';
import { locationsReducer } from './admin/locations/state/locations.reducer';
import { processPartnersReducer } from './admin/process-partners/state/process-partners.reducer';
import { vehicleMovementsReducer } from './admin/vehicle-movements/state/vehicle-movements.reducer';
import { partsManagerReducer } from './admin/parts-manager/state/parts-manager.reducer';
import { countriesReducer } from './admin/countries/state/countries.reducer';
import { uploadFilesReducer } from './shared/upload-files/state/upload-files.reducer';
import { damageEntryReducer } from './damages/state/damages.reducer';
import { countryOptionsReducer } from './state/country-options/country-options.reducer';
import { errorInterceptor } from './common/error.interceptor';
import { routes } from './app.routes';
import { AppTitleStrategy } from './shared/title/app-title.strategy';
import { roleOptionsReducer } from './state/role-options/role-options.reducer';
import { recentSurveysReducer } from './damages/state/recent-surveys.reducer';
import { resolveAuthEnv, resolveAuthInitOptions } from './common/auth.utils';
import { countryRoutes } from './admin/countries/countries.routes';
import { initRoles } from './common/init-roles';
import { liabilitiesReducer } from './liabilities/state/liabilities.reducer';
import { LiabilitiesEffects } from './liabilities/state/liabilities.effects';
import { reportingReducer } from './reporting/state/reporting.reducer';
import { ReportingEffects } from './reporting/state/reporting.effects';
import { workingQueuesReducer } from './state/working-queues/working-queues.reducer';
import { WorkingQueuesEffects } from './state/working-queues/working-queues.effects';
import { dashboardMessagesReducer } from './dashboard/state/dashboard-messages.reducer';
import { DashboardMessagesEffects } from './dashboard/state/dashboard-messages.effects';
import { helpDocumentsReducer } from './header/state/help-documents.reducer';
import { HelpDocumentsEffects } from './header/state/help-documents.effects';
import { backendResolverInterceptor } from './common/backend-resolver.interceptor';
import { writeAuthInterceptor } from './common/write-auth.interceptor';
import { tokenValidityInterceptor } from './common/token-validity.interceptor';
import { restartLoginOnSessionExpiry } from './common/session-expiry';
import { retryUserInfoOnTokenReceived } from './common/user-info-recovery';
import { GlobalErrorHandler } from './common/global-error-handler';

const runtimeChecks: RuntimeChecks = {
  strictStateImmutability: true,
  strictActionImmutability: true,
  strictStateSerializability: false,
  strictActionSerializability: false,
  strictActionWithinNgZone: true,
  strictActionTypeUniqueness: true,
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Required by BMW DS components, which still use the legacy `[@trigger]` animation API.
    // Swap to `animate.enter`/`animate.leave` once BMW DS migrates off the deprecated module.
    provideAnimationsAsync(),
    provideRouter([...routes, ...countryRoutes], withComponentInputBinding()),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(
      withInterceptors([
        backendResolverInterceptor,
        writeAuthInterceptor,
        tokenValidityInterceptor,
        errorInterceptor,
      ]),
      withInterceptorsFromDi()
    ),
    provideFedevAuth({
      env: resolveAuthEnv(environment),
      includeAccessTokenInBackendRequests: true,
      customUrlValidation: (url) =>
        !url.includes('oauth2') &&
        !url.includes('emea') &&
        !url.endsWith('/api/translations'),
    }),
    {
      provide: FEDEV_AUTH_INIT_OPTIONS,
      useValue: resolveAuthInitOptions(environment, {
        config: {
          scope: 'openid profile email phone groups',
          timeoutFactor: 0.75,
        },
      }),
    },
    provideAppInitializer(() => {
      localStorage.removeItem('state');

      const pendingRedirect = sessionStorage.getItem('fedev-auth-redirect');
      if (pendingRedirect) {
        sessionStorage.setItem('tqc-post-auth-redirect', pendingRedirect);
      }

      const authSvc = inject(FedevAuthService);
      const location = inject(Location);
      const router = inject(Router);
      const opts = inject(FEDEV_AUTH_INIT_OPTIONS);
      return authInitializerFactory(authSvc, location, opts).then(() => {
        authSvc.setupAutomaticSilentRefresh();
        restartLoginOnSessionExpiry(authSvc, router);
        retryUserInfoOnTokenReceived(authSvc);
      });
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const store = inject<Store<AppState>>(Store);
      return initTranslations(translate, store)();
    }),
    provideAppInitializer(() => {
      const store = inject<Store<AppState>>(Store);
      return initRoles(store)();
    }),
    importProvidersFrom(
      DsFormFieldModule.forRoot({ markRequiredFormLabels: true }),
      DsImprintModule.forRoot({
        phone: '+49-89-382-55555',
        electronicContact: 'tqc-support@bmw.de',
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useExisting: CustomTranslateLoaderService,
        },
        fallbackLang: 'en',
      })
    ),
    provideStore(
      {
        auth: currentUserReducer,
        translations: translationsReducer,
        users: usersReducer,
        locations: locationsReducer,
        processPartners: processPartnersReducer,
        vehicleMovements: vehicleMovementsReducer,
        partsManager: partsManagerReducer,
        countriesManager: countriesReducer,
        damageEntry: damageEntryReducer,
        uploadFiles: uploadFilesReducer,
        countryOptions: countryOptionsReducer,
        recentSurveys: recentSurveysReducer,
        roleOptions: roleOptionsReducer,
        liabilities: liabilitiesReducer,
        reporting: reportingReducer,
        workingQueues: workingQueuesReducer,
        dashboardMessages: dashboardMessagesReducer,
        helpDocuments: helpDocumentsReducer,
      },
      { runtimeChecks }
    ),
    provideEffects([
      CurrentUserEffects,
      TranslationsEffects,
      UsersEffects,
      LocationsEffects,
      ProcessPartnersEffects,
      VehicleMovementsEffects,
      PartsManagerEffects,
      CountriesEffects,
      UploadFilesEffects,
      DamageEntryEffects,
      CountryOptionsEffects,
      RecentSurveysEffects,
      RoleOptionsEffects,
      LiabilitiesEffects,
      ReportingEffects,
      WorkingQueuesEffects,
      DashboardMessagesEffects,
      HelpDocumentsEffects,
    ]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
